import { type ComponentType, useCallback } from 'react'
import { ArrowLeft, DoorOpen, Play, RotateCcw, Trophy, UserPlus, X } from 'lucide-react'
import { UserAvatar } from '@/components/composites/user-avatar'
import { Button } from '@/components/ui/button'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { useCloseTable, useJoin, useLeave, useMove, useRematch, useStart, useTable, type TableDetail } from './api'
import { Battleship, Memory, Rps } from './boards/hidden'
import { Checkers, ConnectFour, Gomoku, Reversi, TicTacToe } from './boards/grid'
import { Hangman, Pig } from './boards/luck'
import { Dots, Mancala } from './boards/pits'
import { Drawing } from './boards/drawing'
import { Trivia } from './boards/trivia'
import type { BoardProps } from './boards/types'
import { firstName, joinNames, outcomeSentence, openSeats, seatStyle, type GamePerson } from './helpers'

// Each board knows the shape of its own game's view; the table only hands it over.
/* eslint-disable @typescript-eslint/no-explicit-any */
const BOARDS: Record<string, ComponentType<BoardProps<any>>> = {
  tictactoe: TicTacToe,
  connect4: ConnectFour,
  gomoku: Gomoku,
  reversi: Reversi,
  checkers: Checkers,
  mancala: Mancala,
  dots: Dots,
  rps: Rps,
  battleship: Battleship,
  memory: Memory,
  pig: Pig,
  hangman: Hangman,
  trivia: Trivia,
  drawing: Drawing,
}

function Seat({ id, seat, started, people, me, turn, label, gone }: { id: string; seat: number; started: boolean; people: Map<string, GamePerson>; me: string; turn: boolean; label: boolean; gone: boolean }) {
  const p = people.get(id)
  const style = seatStyle(seat)
  return (
    <li className={cn('flex items-center gap-2 rounded-full border bg-card py-1 pl-1 pr-3 text-sm', turn ? cn(style.border, style.soft, 'font-semibold') : 'border-border', gone && 'opacity-50')}>
      <UserAvatar person={{ id, displayName: p?.displayName ?? 'Someone', photoUrl: p?.photoUrl }} size="sm" />
      <span className="truncate">{id === me ? 'You' : firstName(p)}</span>
      {started ? <span className={cn('size-2.5 shrink-0 rounded-full', style.bg)} aria-label={`${style.name} seat`} /> : null}
      {turn && label ? <span className="text-xs font-medium text-text-secondary">to play</span> : null}
      {gone ? <span className="text-xs text-text-muted">left</span> : null}
    </li>
  )
}

/** One table: who is sitting, the board, and what you can do about it. */
export function TableView({ host, me, people, onBack }: { host: string; me: string; people: Map<string, GamePerson>; onBack: () => void }) {
  // The faster a game changes under your feet, the faster it is asked after.
  const query = useTable(host, 1_200)
  const loaded = query.data
  const t: TableDetail | undefined = loaded?.table
  const join = useJoin(host)
  const leave = useLeave(host)
  const start = useStart(host)
  const rematch = useRematch(host)
  const close = useCloseTable(host)
  const move = useMove(host)
  const { refetch } = query
  const refresh = useCallback(() => void refetch(), [refetch])
  const send = useCallback((action: Record<string, unknown>) => move.mutate(action), [move])

  if (query.isError || (!t && !query.isLoading)) {
    return (
      <div className="grid place-items-center gap-3 py-16 text-center">
        <p className="text-sm text-text-secondary">This table has closed.</p>
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="size-4" /> Back to the games
        </Button>
      </div>
    )
  }
  if (!t || !loaded) return <p className="py-16 text-center text-sm text-text-muted">Opening the table…</p>

  const isHost = t.host === me
  const seated = t.players.includes(me) && !t.gone.includes(me)
  const started = t.status !== 'lobby'
  const Board = BOARDS[t.type]
  const hostName = isHost ? 'Your' : `${firstName(people.get(t.host))}’s`
  const needed = Math.max(0, t.min - t.players.length)
  const waitingNames = t.waiting.map((s) => (t.players[s] === me ? 'you' : firstName(people.get(t.players[s] ?? ''))))
  const myTurn = t.seat !== null && t.waiting.includes(t.seat)

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} className="-ml-2">
          <ArrowLeft className="size-4" /> All games
        </Button>
        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-base font-semibold">
          <span aria-hidden className="text-xl">{t.game.emoji}</span>
          <span className="truncate">
            {t.game.name} <span className="font-normal text-text-muted">· {hostName} table</span>
          </span>
        </h2>
        {seated && t.status !== 'closed' ? (
          t.status === 'playing' ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="outline" size="sm">
                  <DoorOpen className="size-4" /> Leave
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Leave this game?</AlertDialogTitle>
                  <AlertDialogDescription>{t.players.length === 2 ? 'Leaving counts as giving up: the other player wins.' : 'Leaving ends the game for everyone at the table.'}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Stay</AlertDialogCancel>
                  <AlertDialogAction onClick={() => leave.mutate(undefined, { onSuccess: onBack })}>Leave the game</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button type="button" variant="outline" size="sm" disabled={leave.isPending} onClick={() => leave.mutate(undefined, { onSuccess: onBack })}>
              <DoorOpen className="size-4" /> {isHost && t.status === 'lobby' ? 'Close table' : 'Leave'}
            </Button>
          )
        ) : null}
      </div>

      <ul className="flex flex-wrap items-center gap-2" aria-label="Players">
        {t.players.map((id, s) => (
          <Seat key={id} id={id} seat={s} started={started} people={people} me={me} turn={t.status === 'playing' && t.waiting.includes(s)} label={t.waiting.length === 1} gone={t.gone.includes(id)} />
        ))}
        {t.status === 'lobby'
          ? Array.from({ length: Math.min(openSeats(t), 3) }, (_, i) => (
              <li key={`open${i}`} className="rounded-full border border-dashed border-border-strong px-3 py-1.5 text-sm text-text-muted">
                open seat
              </li>
            ))
          : null}
      </ul>

      {t.status === 'lobby' ? (
        <div className="grid place-items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-10 text-center">
          <span className="text-5xl" aria-hidden>{t.game.emoji}</span>
          <p className="max-w-sm text-sm text-text-secondary">{t.game.blurb}</p>
          <p className="text-sm font-medium">
            {t.players.length} of {t.max === t.min ? t.max : `${t.min} to ${t.max}`} seated
            {needed ? ` · waiting for ${needed} more` : ''}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {!seated && openSeats(t) > 0 ? (
              <Button type="button" onClick={() => join.mutate()} disabled={join.isPending}>
                <UserPlus className="size-4" /> Take a seat
              </Button>
            ) : null}
            {isHost ? (
              <Button type="button" onClick={() => start.mutate()} disabled={needed > 0 || start.isPending}>
                <Play className="size-4" /> Start the game
              </Button>
            ) : null}
            {isHost ? (
              <Button type="button" variant="outline" onClick={() => close.mutate(undefined, { onSuccess: onBack })}>
                <X className="size-4" /> Close table
              </Button>
            ) : null}
          </div>
          {!seated && openSeats(t) === 0 ? <p className="text-xs text-text-muted">This table is full. You can watch when it starts.</p> : null}
          {seated && !isHost ? <p className="text-xs text-text-muted">Waiting for {firstName(people.get(t.host))} to start.</p> : null}
        </div>
      ) : (
        <>
          {t.status === 'over' && t.outcome ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-success/40 bg-success-soft px-4 py-3">
              <Trophy className="size-5 shrink-0 text-success" aria-hidden />
              <p className="min-w-0 flex-1 text-sm font-semibold">{outcomeSentence(t.outcome, t.players, people, me)}</p>
              {isHost ? (
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" onClick={() => rematch.mutate()} disabled={rematch.isPending || t.gone.includes(me)}>
                    <RotateCcw className="size-4" /> Play again
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => close.mutate(undefined, { onSuccess: onBack })}>
                    Close table
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-text-secondary">{firstName(people.get(t.host))} can set up another game.</p>
              )}
            </div>
          ) : (
            <p className={cn('rounded-lg px-3 py-1.5 text-center text-sm font-medium', myTurn ? 'bg-brand text-text-inverse' : 'bg-muted text-text-secondary')} aria-live="polite">
              {myTurn ? (t.waiting.length > 1 ? 'Your move' : 'Your turn') : t.waiting.length ? `Waiting for ${joinNames(waitingNames)}` : seated ? 'Next up in a moment…' : 'Watching'}
            </p>
          )}
          {Board && t.view ? (
            <Board view={t.view} seat={t.seat} players={t.players} people={people} over={t.status === 'over'} busy={move.isPending && t.type !== 'drawing'} send={send} skew={loaded.skew} refresh={refresh} />
          ) : null}
          {!seated ? <p className="text-center text-xs text-text-muted">You are watching this game.</p> : null}
        </>
      )}
    </div>
  )
}
